import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { BarChart2 } from "lucide-react";
import type { BenchmarkResponse } from "@/services/analyticsService";
import { UnifiedCard } from "./UnifiedCard";

export interface BenchmarkChartsProps {
  benchmarks: BenchmarkResponse[];
  selectedOfferingType: string;
}

interface ChartSpec {
  key: string;
  title: string;
  unit: "%" | "";
  you: number;
  median: number;
  p75: number;
  ariaLabel: string;
}

/**
 * Three small-multiple charts per benchmark entry (response rate %, click-through
 * rate %, inquiry volume count) — small multiples were chosen over one normalized
 * chart specifically because Recharts' `yAxisId` binds per-series not per-datum,
 * ruling out mixed-unit (%/%/count) single charts without index-normalizing, which
 * was rejected during architecture review.
 *
 * The inquiry-volume chart is the actual bug fix this component includes: the
 * current page fetches `yourInquiryVolume`/`cohortMedianInquiryVolume`/
 * `cohortP75InquiryVolume` but never renders them. Each chart auto-scales to its own
 * real unit — no shared/normalized axis, no `max === 0` guard needed.
 */
function buildChartSpecs(b: BenchmarkResponse): ChartSpec[] {
  return [
    {
      key: "response-rate",
      title: "Response Rate",
      unit: "%",
      you: +(b.yourResponseRate * 100).toFixed(1),
      median: +(b.cohortMedianResponseRate * 100).toFixed(1),
      p75: +(b.cohortP75ResponseRate * 100).toFixed(1),
      ariaLabel: "Response rate percentage, you vs. cohort median and top quartile",
    },
    {
      key: "click-through-rate",
      title: "Click-Through Rate",
      unit: "%",
      you: +(b.yourClickThroughRate * 100).toFixed(1),
      median: +(b.cohortMedianClickThroughRate * 100).toFixed(1),
      p75: +(b.cohortP75ClickThroughRate * 100).toFixed(1),
      ariaLabel: "Click-through rate percentage, you vs. cohort median and top quartile",
    },
    {
      key: "inquiry-volume",
      title: "Inquiry Volume",
      unit: "",
      you: b.yourInquiryVolume,
      median: b.cohortMedianInquiryVolume,
      p75: b.cohortP75InquiryVolume,
      ariaLabel: "Inquiry volume count, you vs. cohort median and top quartile",
    },
  ];
}

function BenchmarkChart({ spec }: { spec: ChartSpec }) {
  const data = [{ name: spec.title, You: spec.you, Median: spec.median, "Top 25%": spec.p75 }];

  return (
    <div className="rounded-xl bg-slate-50 p-3">
      <p className="text-xs font-semibold text-slate-700 mb-3">{spec.title}</p>
      {/* Accessible name on the wrapping element — charts alone are not
          screen-reader-accessible. */}
      <div role="img" aria-label={spec.ariaLabel}>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} barCategoryGap="30%">
            <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} unit={spec.unit} axisLine={false} tickLine={false} width={36} />
            <Tooltip
              formatter={(v: number) => `${v}${spec.unit}`}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
            <Bar dataKey="You" fill="#6366f1" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Median" fill="#94a3b8" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Top 25%" fill="#34d399" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/**
 * Renders ONLY the benchmark entries whose `offeringType === selectedOfferingType`
 * (round-3 architecture-review fix for a real chart-explosion bug — a teacher with
 * several subjects across both offering types could otherwise produce 8+ entries x 3
 * charts = 24+ chart instances on one page).
 */
export function BenchmarkCharts({ benchmarks, selectedOfferingType }: BenchmarkChartsProps) {
  const scoped = benchmarks.filter((b) => b.offeringType === selectedOfferingType);
  if (scoped.length === 0) return null;

  return (
    <UnifiedCard icon={BarChart2} title="Benchmark Comparison">
      <div className="space-y-6">
        {scoped.map((b) => {
          const entryKey = `${b.subject}-${b.offeringType}`;

          if (!b.cohortMinimumMet) {
            return (
              <div key={entryKey}>
                <p className="text-sm font-medium mb-1">
                  {b.subject} — {b.offeringType.replace(/_/g, "-")}
                </p>
                <p className="text-xs text-muted-foreground">
                  Not enough data for reliable comparison (only {b.cohortSize} teacher
                  {b.cohortSize !== 1 ? "s" : ""} in this cohort).
                </p>
              </div>
            );
          }

          const specs = buildChartSpecs(b);

          return (
            <div key={entryKey}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm font-semibold text-slate-800">
                  {b.subject} — {b.offeringType.replace(/_/g, "-")}
                </span>
                <span className="ml-auto text-xs text-muted-foreground bg-slate-100 px-2 py-0.5 rounded-full">
                  vs. {b.cohortSize} teachers
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {specs.map((spec) => (
                  <BenchmarkChart key={spec.key} spec={spec} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </UnifiedCard>
  );
}

export default BenchmarkCharts;
